// src/pages/KarigarManagement/Payment/PaymentListPage.jsx
// Fixed: Added approve/reject handlers for pending payments

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { usePaymentManagement } from "@/hooks/useKarigar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui-v2/table";
import { Button } from "@/components/ui-v2/button";
import { Input } from "@/components/ui-v2/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui-v2/select";
import { Badge } from "@/components/ui-v2/badge";
import { Card, CardContent } from "@/components/ui-v2/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui-v2/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui-v2/alert-dialog";
import { Textarea } from "@/components/ui-v2/textarea";
import { Label } from "@/components/ui-v2/label";
import {
  Search,
  MoreVertical,
  Eye,
  CheckCircle,
  XCircle,
  RefreshCw,
  Clock,
  IndianRupee,
  CreditCard,
  Loader2,
} from "lucide-react";
import Pagination from "@/components/common/Pagination";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { toast } from "sonner";
import { debounce } from "lodash";
import { format } from "date-fns";

// Status badge colors - handle both cases
const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800",
  approved: "bg-blue-100 text-blue-800",
  processing: "bg-purple-100 text-purple-800",
  processed: "bg-green-100 text-green-800",
  paid: "bg-green-200 text-green-900",
  completed: "bg-green-200 text-green-900",
  rejected: "bg-red-100 text-red-800",
  cancelled: "bg-gray-100 text-gray-800",
  PENDING: "bg-yellow-100 text-yellow-800",
  APPROVED: "bg-blue-100 text-blue-800",
  PROCESSING: "bg-purple-100 text-purple-800",
  PROCESSED: "bg-green-100 text-green-800",
  PAID: "bg-green-200 text-green-900",
  COMPLETED: "bg-green-200 text-green-900",
  REJECTED: "bg-red-100 text-red-800",
  CANCELLED: "bg-gray-100 text-gray-800",
};

const PaymentListPage = () => {
  const navigate = useNavigate();
  const {
    payments,
    pagination,
    filters,
    loading,
    error,
    successMessage,
    pendingCount,
    pendingTotal,
    getPayments,
    getPendingPayments,
    approve,
    reject,
    updateFilters,
    clearFilters,
    clearError,
    clearSuccessMessage,
  } = usePaymentManagement();

  const [searchInput, setSearchInput] = useState(filters.search || "");
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [approveNotes, setApproveNotes] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((value) => {
      updateFilters({ search: value });
    }, 500),
    []
  );

  // Fetch payments on mount and filter change
  useEffect(() => {
    getPayments({
      page: pagination.page || 1,
      limit: pagination.limit || 20,
      ...filters,
    });
    getPendingPayments();
  }, [filters, pagination.page]);

  // Handle success/error messages
  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      clearSuccessMessage();
      // Refresh data
      getPayments({
        page: pagination.page || 1,
        limit: pagination.limit || 20,
        ...filters,
      });
      getPendingPayments();
    }
    if (error) {
      toast.error(error);
      clearError();
    }
  }, [successMessage, error]);

  // Handle search input
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    debouncedSearch(value);
  };

  // Handle filter changes
  const handleStatusFilter = (value) => {
    updateFilters({ status: value === "all" ? "" : value });
  };

  // Handle page change
  const handlePageChange = (page) => {
    getPayments({
      page,
      limit: pagination.limit || 20,
      ...filters,
    });
  };

  // Reset filters
  const handleResetFilters = () => {
    setSearchInput("");
    clearFilters();
  };

  // Open approve dialog
  const openApproveDialog = (payment) => {
    setSelectedPayment(payment);
    setApproveNotes("");
    setShowApproveDialog(true);
  };

  // Open reject dialog
  const openRejectDialog = (payment) => {
    setSelectedPayment(payment);
    setRejectReason("");
    setShowRejectDialog(true);
  };

  // Handle approve
  const handleApprove = async () => {
    if (!selectedPayment) return;
    setActionLoading(true);
    try {
      await approve(selectedPayment._id, approveNotes || undefined);
      setShowApproveDialog(false);
      setSelectedPayment(null);
    } catch (err) {
      // Error handled by useEffect
    } finally {
      setActionLoading(false);
    }
  };

  // Handle reject
  const handleReject = async () => {
    if (!selectedPayment || !rejectReason.trim()) return;
    setActionLoading(true);
    try {
      await reject(selectedPayment._id, rejectReason);
      setShowRejectDialog(false);
      setSelectedPayment(null);
    } catch (err) {
      // Error handled by useEffect
    } finally {
      setActionLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  // Check if can be approved/rejected
  const canApprove = (status) => {
    const s = status?.toLowerCase();
    return s === "pending";
  };

  // Calculate processed today
  const processedToday = payments.filter(
    (p) =>
      (p.status?.toLowerCase() === "processed" ||
        p.status?.toLowerCase() === "paid") &&
      p.processedAt &&
      new Date(p.processedAt).toDateString() === new Date().toDateString()
  ).length;

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Karigar Payments</h1>
          <p className="text-muted-foreground">
            Manage karigar payments and earnings
          </p>
        </div>
        <Button
          onClick={() => navigate("/karigar-management/payments/pending")}
        >
          <Clock className="h-4 w-4 mr-2" />
          Pending Payments ({pendingCount || 0})
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-yellow-500" />
              <div>
                <p className="text-sm text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold">{pendingCount || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <IndianRupee className="h-5 w-5 text-orange-500" />
              <div>
                <p className="text-sm text-muted-foreground">Pending Amount</p>
                <p className="text-2xl font-bold">{pendingTotal}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <div>
                <p className="text-sm text-muted-foreground">Processed Today</p>
                <p className="text-2xl font-bold">{processedToday}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-blue-500" />
              <div>
                <p className="text-sm text-muted-foreground">Total Payments</p>
                <p className="text-2xl font-bold">{pagination.total || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[250px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by payment ID, karigar..."
                value={searchInput}
                onChange={handleSearchChange}
                className="pl-10"
              />
            </div>

            {/* Status Filter */}
            <Select
              value={filters.status || "all"}
              onValueChange={handleStatusFilter}
            >
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="processed">Processed</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>

            {/* Reset Filters */}
            <Button variant="outline" onClick={handleResetFilters}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Reset
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading.list ? (
            <div className="flex justify-center items-center h-64">
              <LoadingSpinner />
            </div>
          ) : !payments || payments.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <p>No payments found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Payment ID</TableHead>
                  <TableHead>Karigar</TableHead>
                  <TableHead>Job</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.map((payment) => (
                  <TableRow key={payment._id}>
                    {/* Payment ID */}
                    <TableCell>
                      <p className="font-medium">
                        {payment.paymentNumber || payment._id?.slice(-8)}
                      </p>
                    </TableCell>

                    {/* Karigar */}
                    <TableCell>
                      <div>
                        <p className="font-medium">
                          {payment.karigar?.name ||
                            payment.karigarId?.name ||
                            "-"}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {payment.karigar?.karigarCode ||
                            payment.karigarId?.karigarCode ||
                            ""}
                        </p>
                      </div>
                    </TableCell>

                    {/* Job */}
                    <TableCell>
                      {payment.job || payment.jobId ? (
                        <p className="text-sm">
                          {payment.job?.jobNumber ||
                            payment.jobId?.jobNumber ||
                            "-"}
                        </p>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>

                    {/* Amount */}
                    <TableCell>
                      <p className="font-semibold">
                        {formatCurrency(
                          payment.amount ||
                            payment.grossAmount ||
                            payment.netAmount
                        )}
                      </p>
                      {payment.deductions > 0 && (
                        <p className="text-xs text-red-500">
                          -{formatCurrency(payment.deductions)}
                        </p>
                      )}
                    </TableCell>

                    {/* Type */}
                    <TableCell>
                      <Badge variant="outline">
                        {payment.paymentType?.replace(/_/g, " ")}
                      </Badge>
                    </TableCell>

                    {/* Date */}
                    <TableCell>
                      {payment.createdAt
                        ? format(new Date(payment.createdAt), "dd MMM yyyy")
                        : "-"}
                    </TableCell>

                    {/* Status */}
                    <TableCell>
                      <Badge
                        className={
                          STATUS_COLORS[payment.status] || "bg-gray-100"
                        }
                      >
                        {payment.status}
                      </Badge>
                    </TableCell>

                    {/* Actions */}
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() =>
                              navigate(
                                `/karigar-management/payments/${payment._id}`
                              )
                            }
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>

                          {canApprove(payment.status) && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-green-600"
                                onClick={() => openApproveDialog(payment)}
                              >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Approve
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-red-600"
                                onClick={() => openRejectDialog(payment)}
                              >
                                <XCircle className="h-4 w-4 mr-2" />
                                Reject
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="border-t p-4">
            <Pagination
              currentPage={pagination.page || 1}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </Card>

      {/* Approve Dialog */}
      <AlertDialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Approve Payment</AlertDialogTitle>
            <AlertDialogDescription>
              Approve payment of {formatCurrency(selectedPayment?.amount)} to{" "}
              {selectedPayment?.karigar?.name ||
                selectedPayment?.karigarId?.name ||
                "karigar"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label>Notes (Optional)</Label>
            <Textarea
              placeholder="Approval notes..."
              value={approveNotes}
              onChange={(e) => setApproveNotes(e.target.value)}
              className="mt-2"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleApprove}
              disabled={actionLoading}
              className="bg-green-600 hover:bg-green-700"
            >
              {actionLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Approving...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reject Dialog */}
      <AlertDialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reject Payment</AlertDialogTitle>
            <AlertDialogDescription>
              Reject payment of {formatCurrency(selectedPayment?.amount)} to{" "}
              {selectedPayment?.karigar?.name ||
                selectedPayment?.karigarId?.name ||
                "karigar"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label>Reason for Rejection *</Label>
            <Textarea
              placeholder="Enter reason for rejection..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="mt-2"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReject}
              disabled={actionLoading || !rejectReason.trim()}
              className="bg-red-600 hover:bg-red-700"
            >
              {actionLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Rejecting...
                </>
              ) : (
                <>
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PaymentListPage;
